








































import useAlgoliaSearchStore from '@algolia-search/stores/algoliaSearch';
import {
  computed,
  defineComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  useContext,
  useRouter
} from '@nuxtjs/composition-api';
import { onSSR } from '@vue-storefront/core';
import {
  useDebounceFn,
  useEventListener,
  useMutationObserver,
  useThrottleFn
} from '@vueuse/core';
import { storeToRefs } from 'pinia';

import { useMegaMenuMobileStore } from '@/diptyqueTheme/stores/megaMenuMobile';
import { useLink, useScreenSize } from '~/diptyqueTheme/composable';
import { useThemeUiState } from '~/diptyqueTheme/composable/useThemeUiState';
import type { NavItem } from '~/diptyqueTheme/types/components';
import { useContentfulGQL } from '~/integrations/contentful/src/composables/useContentfulGQL';

export default defineComponent({
  name: 'VaimoMobileMenu',
  components: {
    VaimoHeaderSearch: () =>
      import('organisms/header/mobile/VaimoHeaderSearch.vue'),
    VaimoMenuLayerMobile: () =>
      import('organisms/header/mobile/VaimoMenuLayerMobile.vue'),
    VaimoHeaderNavigation: () =>
      import('organisms/header/mobile/VaimoHeaderNavigation.vue'),
    VaimoAvailableStores: () =>
      import('organisms/VaimoStoreSwitcher/VaimoAvailableStores.vue')
  },
  scrollToTop: true,
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  emits: ['menu-closed'],

  setup(props) {
    const { app } = useContext();
    const $router = useRouter();
    const { normalizeLink, isInternal } = useLink();
    const { isDesktop } = useScreenSize();
    const megaMenuMobileStore = useMegaMenuMobileStore();
    const { isOpen: isMobileMenuVisible, isMenuLayersVisible } =
      storeToRefs(megaMenuMobileStore);
    const searchStore = useAlgoliaSearchStore();
    const { isSearchModalOpen } = storeToRefs(searchStore);
    const { isMobileStoreSwitcherOpen, closeMobileStoreSwitcher } =
      useThemeUiState();
    const { query, queryResponse: megaMenuEntries } =
      useContentfulGQL('byIdMobileMenu');
    const currentNav = ref<NavItem>({
      id: '',
      text: '',
      link: ''
    });
    const mobileMenuRef = ref<HTMLElement | null>(null);
    const mobileHeaderRef = ref<HTMLElement | null>(null);

    // Called after the 'before-enter' hook, during the enter phase
    const onTransitionBeforeEnter = () => {
      if (!process.client) return;

      document.body.classList.add('no-scroll');
    };

    // Lock body scroll after completing enter transition
    // - timing is important to avoid white background flash
    const onTransitionAfterEnter = () => {
      if (!process.client) return;

      lockBodyScroll();
    };

    // Unlock body scroll before starting leave transition
    // - timing is important to avoid white background flash
    const onTransitionBeforeLeave = () => {
      if (!process.client) return;

      unlockBodyScroll();
    };

    // Called after the 'before-leave' hook, during the leave phase
    const onTransitionAfterLeave = () => {
      if (!process.client) return;

      document.body.classList.remove('no-scroll');
    };

    const WINDOW_SCROLL_THROTTLE = 100;
    const WINDOW_RESIZE_DEBOUNCE = 200;

    onMounted(() => {
      const throttledWindowScroll = useThrottleFn(() => {
        document.documentElement.style.setProperty(
          '--body-scroll-y',
          `${window.scrollY}px`
        );
      }, WINDOW_SCROLL_THROTTLE);

      const debouncedWindowResize = useDebounceFn(() => {
        if (!isDesktop.value && props.visible && mobileMenuRef.value) {
          lockBodyScroll();
        } else if (isDesktop.value) {
          unlockBodyScroll();
        }
      }, WINDOW_RESIZE_DEBOUNCE);

      useEventListener(window, 'scroll', throttledWindowScroll);
      useEventListener(window, 'resize', debouncedWindowResize);
    });

    const lockBodyScroll = () => {
      // Get the current scroll position
      const scrollY =
        document.documentElement.style.getPropertyValue('--body-scroll-y');

      // Fix body position
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}`;
    };

    const unlockBodyScroll = () => {
      const scrollBehaviour = document.documentElement.style.scrollBehavior;
      const scrollY = document.body.style.top;

      // Restore body position
      document.body.style.position = '';
      document.body.style.top = '';

      // Temporarily disable smooth scroll behavior to avoid smooth scrolling
      document.documentElement.style.scrollBehavior = 'auto';

      // Restore the scroll position
      window.scrollTo({
        top: parseInt(scrollY || '0') * -1,
        left: 0,
        behavior: 'auto'
      });

      // Restore the original scroll behavior
      document.documentElement.style.scrollBehavior = scrollBehaviour;

      // Remove no-scroll class from body (if present)
      document.body.classList.remove('no-scroll');
    };

    onBeforeUnmount(() => {
      unlockBodyScroll();
    });

    /**
     * Determines if the given navigation item is the currently active menu layer.
     *
     * @param {NavItem} menuLayer - The navigation item to check.
     * @returns {boolean} True if the navigation item's id matches the current navigation id, otherwise false.
     */
    const isCurrentMenuLayer = (menuLayer: NavItem): boolean => {
      return currentNav.value?.id === menuLayer.id;
    };

    /**
     * Navigates to the link specified in the given navigation item.
     * If the link is internal, uses Vue Router to navigate.
     * If the link is external, sets the window location to the link.
     *
     * @param {NavItem} item - The navigation item containing the link to navigate to.
     */
    const navigateToLink = (item: NavItem) => {
      if (item.link && item.link !== '#') {
        isInternal(item.link)
          ? $router.push(normalizeLink(item.link))
          : (window.location.href = item.link);
      }
    };

    /**
     * Determines if a menu layer associated with the given navigation item has any content blocks.
     *
     * @param {NavItem} item - The navigation item to check for associated menu layer content.
     * @returns {boolean} Returns `true` if the menu layer has at least one content block, otherwise `false`.
     */
    const getMenuLayerHasContent = (item: NavItem): boolean => {
      const menuLayers = menuLayersList.value.filter(
        (layer) => layer.id === item.id
      );
      return !!menuLayers[0]?.data?.filter((block) => block).length;
    };

    /**
     * Handles navigation changes when a menu item is selected.
     * - Sets the current navigation item.
     * - Shows menu layers if the selected item has content.
     * - Otherwise, hides the menu and menu layers, then navigates to the item's link.
     *
     * @param {NavItem} item - The navigation item that was selected.
     */
    const handleNavigationChange = (item: NavItem) => {
      currentNav.value = item;
      if (getMenuLayerHasContent(item)) {
        megaMenuMobileStore.showMenuLayers();
      } else {
        megaMenuMobileStore.hide();
        megaMenuMobileStore.hideMenuLayers();
        navigateToLink(item);
      }
    };

    /**
     * Sets the top position of the mobile menu based on the height of the mobile header.
     * This ensures that the mobile menu is positioned correctly below the header.
     */
    const setMenuTop = () => {
      if (mobileHeaderRef.value && mobileMenuRef.value) {
        const height = mobileHeaderRef.value.clientHeight;
        mobileMenuRef.value.style.top = height > 0 ? `${height}px` : '0';
      }
    };

    onMounted(() => {
      nextTick(() => {
        // Reference to a mobile header element
        mobileHeaderRef.value = document.querySelector(
          '.top-header-mobile__wrapper'
        );

        // Initial modile navigation top position setup
        setMenuTop();

        // 1. Watch for child node changes in a mobile header
        // 2. Watch for any changes in the subtree of a mobile header
        useMutationObserver(mobileHeaderRef.value, setMenuTop, {
          childList: true,
          subtree: true
        });
      });
    });

    const hideMenuLayer = (): void => {
      megaMenuMobileStore.hideMenuLayers();
    };

    const megaMenuEntry = computed(() => {
      return { megaMenu: megaMenuEntries.value?.megaMenuCollection?.items[0] };
    });

    const menuLayersList = computed<NavItem[]>(() => {
      const items =
        megaMenuEntry.value?.megaMenu?.layersCollection?.items ?? [];

      return items.reduce((memo, cur) => {
        if (cur && cur.layerContentCollection?.items?.length) {
          memo.push({
            id: cur.sys?.id,
            data: [...cur.layerContentCollection.items]
          });
        }
        return memo;
      }, []);
    });

    onSSR(async () => {
      await query('getMegaMenu', {
        type: 'Header',
        storeview: app.i18n.localeProperties.storeview
      });
    });

    return {
      mobileHeaderRef,
      mobileMenuRef,
      onTransitionBeforeEnter,
      onTransitionAfterEnter,
      onTransitionBeforeLeave,
      onTransitionAfterLeave,
      handleNavigationChange,
      hideMenuLayer,
      isMenuLayersVisible,
      isMobileStoreSwitcherOpen,
      closeMobileStoreSwitcher,
      isMobileMenuVisible,
      currentNav,
      isCurrentMenuLayer,
      megaMenuEntry,
      menuLayersList,
      isSearchModalOpen
    };
  }
});
